import {validateSourceSceneGenerationDocumentStructure} from '../../protocol/source-scene-generation';
import {validateSourceScriptDocument} from '../../protocol/source-script-data';
import {validateSourceBrandIntroDocument} from '../../protocol/source-brand-intro';
import {bindSourceBrandIntroLeadingInterval,sourceBrandIntroItems} from './source-brand-intro';
import {validateSourceSlides} from '../../protocol/source-slide';
import {validateSourceTextDocument} from '../../protocol/source-text';
import {
  copyEditorJson,
  createNativeEnvelope,
  readNativeEnvelope,
  NATIVE_EDITOR_RUNTIME,
  type JsonObject,
  type NativeEditorEnvelope,
} from "../../protocol/native-envelope";
import type {
  EditorState,
  UndoableState,
} from "../../vendor/src/features/remotion-editor/editor/state/types";
import { getInitialState } from "../../vendor/src/features/remotion-editor/editor/state/initial-state";
import { DtoProjectType } from "../../vendor/src/shared/constants";
import { parseClipboardGraph,requiredClipboardVersion } from "./clipboard-graph";
import { hasPendingTimelineEdit } from "./edit-ownership";
import {alignImportedNarration} from './import-narration-cuts';

type Settings = Pick<
  EditorState,
  | "isLinkingEnabled"
  | "isSnappingEnabled"
  | "isSkimmingEnabled"
  | "loop"
  | "brandTemplateId"
  | "editorAppliedAccountBrandTemplateId"
  | "safeZoneGuide"
> & { projectType: DtoProjectType | null };
export type NativeFileState = {
  document: UndoableState;
  /** Server row revision; savedAt is presentation metadata only. */
  revision?: string;
  settings: Settings;
  envelope: NativeEditorEnvelope;
  importNotice?: string;
  requiresInitialSave?: boolean;
};
export const requiresNativeCodeTrust = (value: NativeFileState) =>
  Object.values(value.document.items).some((item) => item.type === "code");
const record = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);
const finite = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);
function readSettings(raw: JsonObject): Settings | null {
  const {
    isLinkingEnabled,
    isSnappingEnabled,
    isSkimmingEnabled,
    loop,
    brandTemplateId,
    editorAppliedAccountBrandTemplateId,
    safeZoneGuide,
    projectType,
  } = raw;
  const reference = (v: unknown): v is number | null =>
    v === null || (typeof v === "number" && Number.isSafeInteger(v) && v >= 0);
  const platform = (
    v: unknown,
  ): v is EditorState["safeZoneGuide"]["platform"] =>
    typeof v === "string" &&
    ["instagram", "tiktok", "youtube", "gsshop"].includes(v);
  const project = (v: unknown): v is DtoProjectType | null =>
    v === null || Object.values(DtoProjectType).some((type) => type === v);
  if (
    typeof isLinkingEnabled !== "boolean" ||
    typeof isSnappingEnabled !== "boolean" ||
    typeof isSkimmingEnabled !== "boolean" ||
    typeof loop !== "boolean" ||
    !reference(brandTemplateId) ||
    !reference(editorAppliedAccountBrandTemplateId) ||
    !record(safeZoneGuide) ||
    typeof safeZoneGuide.enabled !== "boolean" ||
    !platform(safeZoneGuide.platform) ||
    !project(projectType)
  )
    return null;
  return {
    isLinkingEnabled,
    isSnappingEnabled,
    isSkimmingEnabled,
    loop,
    brandTemplateId,
    editorAppliedAccountBrandTemplateId,
    safeZoneGuide: {
      ...safeZoneGuide,
      enabled: safeZoneGuide.enabled,
      platform: safeZoneGuide.platform,
    },
    projectType,
  };
}
function defaults(): Settings {
  const s = getInitialState();
  return {
    isLinkingEnabled: s.isLinkingEnabled,
    isSnappingEnabled: s.isSnappingEnabled,
    isSkimmingEnabled: s.isSkimmingEnabled,
    loop: s.loop,
    brandTemplateId: null,
    editorAppliedAccountBrandTemplateId: null,
    safeZoneGuide: s.safeZoneGuide,
    projectType: DtoProjectType.commerce,
  };
}
export function importedLegacyFile(
  document: UndoableState,
  raw: unknown,
  savedAt: string,
): NativeFileState {
  const aligned = alignImportedNarration(document);
  let imported={...getInitialState(),undoableState:aligned.document};
  for(const card of sourceBrandIntroItems(imported))if(card.sourceBrandIntro?.provenance.kind==='story'&&!card.sourceBrandIntro.leadingInterval)imported=bindSourceBrandIntroLeadingInterval(imported,card.id);
  aligned.document=imported.undoableState;
  const documentKeys = new Set([
    "assets",
    "items",
    "tracks",
    "transitions",
    "fps",
    "compositionWidth",
    "compositionHeight",
    "deletedAssets",
    "backgroundColor",
  ]);
  const metadata = record(raw)
    ? Object.fromEntries(
        Object.entries(raw).filter(([key]) => !documentKeys.has(key)),
      )
    : {};
  const envelope = createNativeEnvelope({
    document: aligned.document,
    settings: defaults(),
    savedAt,
    sourceContext: { importedFrom: "legacy-export", legacyMetadata: metadata },
  });
  const restored = readNativeDocumentFile(envelope);
  if (restored.status !== "ready")
    throw new Error(
      restored.status === "error"
        ? restored.message
        : "편집 자료를 변환하지 못했습니다.",
    );
  return {...restored.value, requiresInitialSave: true, ...(aligned.notice ? {importNotice: aligned.notice} : {})};
}

/** Full document, not a selected clipboard fragment. Unused assets and empty tracks remain included. */
function validNativeDocument(value: unknown): value is UndoableState {
  if(!validateSourceSceneGenerationDocumentStructure(value))return false;
  if(!validateSourceScriptDocument(value)||!validateSourceBrandIntroDocument(value)||!validateSourceTextDocument(value)||!validateSourceSlides(value))return false;
  if(record(value)&&record(value.items)&&Object.values(value.items).some(item=>record(item)&&item.sourceSpeechRestore!==undefined&&(!record(item.sourceSpeechRestore)||!validNativeDocument(item.sourceSpeechRestore.original))))return false;
  if (
    !record(value) ||
    !Array.isArray(value.tracks) ||
    !record(value.items) ||
    !record(value.assets) ||
    !Array.isArray(value.deletedAssets) ||
    typeof value.backgroundColor !== "string"
  )
    return false;
  if (
    ![value.compositionWidth, value.compositionHeight].every(
      (n) => finite(n) && n > 0 && n <= 32768,
    )
  )
    return false;
  const mask = value.mask;
  if (
    mask !== undefined &&
    (!record(mask) ||
      typeof mask.enabled !== "boolean" ||
      !["x", "y", "width", "height"].every((k) => finite(mask[k])) ||
      Number(mask.width) <= 0 ||
      Number(mask.height) <= 0 ||
      !["16:9", "1:1", "9:16", "custom", null].some(
        (r) => r === mask.aspectRatio,
      ))
  )
    return false;
  if (
    !value.deletedAssets.every(
      (d) =>
        record(d) &&
        typeof d.assetId === "string" &&
        record(d.statusAtDeletion),
    )
  )
    return false;
  const roots = value.tracks.flatMap((t) =>
    record(t) && Array.isArray(t.items) ? t.items : [],
  );
  const parsed = parseClipboardGraph(
    {
      compositionWidth:value.compositionWidth,compositionHeight:value.compositionHeight,
      ...(value.sourceSceneGeneration?{sourceSceneGeneration:value.sourceSceneGeneration}:{}),
      format: "waka-editor-clipboard",
      version: requiredClipboardVersion(value),
      fps: value.fps,
      roots,
      items: value.items,
      assets: value.assets,
      tracks: value.tracks,
      transitions: value.transitions,
    },
    { allowEmpty: true },
  );
  if (!parsed) return false;
  for (const item of Object.values(parsed.items)) {
    if (
      item.isDraggingInTimeline ||
      !Number.isSafeInteger(item.from + item.durationInFrames) ||
      item.opacity < 0 ||
      item.opacity > 1
    )
      return false;
    if (
      [
        "video",
        "image",
        "gif",
        "webp",
        "text",
        "captions",
        "group",
        "code",
        "pdf",
        "ppt",
      ].includes(item.type) &&
      (item.width <= 0 || item.height <= 0)
    )
      return false;
    if (
      item.type === "captions" &&
      (item.captionStartInSeconds < 0 ||
        item.pageDurationInMilliseconds <= 0 ||
        !Number.isInteger(item.maxLines) ||
        item.maxLines <= 0 ||
        item.fontSize <= 0 ||
        item.lineHeight <= 0)
    )
      return false;
  }
  // Unknown native presentation/context fields remain intact after validating the shared graph boundary.
  return true;
}
export function parseNativeDocument(value: unknown): UndoableState | null {
  return validNativeDocument(value) ? value : null;
}

function portableAssets(document: UndoableState): string[] {
  return Object.values(document.assets)
    .filter((a) => {
      if (a.type === "caption") return false;
      if (typeof a.remoteUrl !== "string") return true;
      if (/^data:(image|audio|video)\//.test(a.remoteUrl)) return false;
      try {
        const url = new URL(a.remoteUrl);
        return url.protocol !== "https:" || !!url.username || !!url.password;
      } catch {
        return true;
      }
    })
    .map((a) => a.filename || a.id);
}

export function createNativeDocumentFile(
  state: EditorState,
  options: {
    savedAt: string;
    projectType: DtoProjectType | null;
    previous?: NativeEditorEnvelope | null;
  },
): NativeEditorEnvelope {
  if (hasPendingTimelineEdit(state))
    throw new Error("이동이나 시간 입력을 마친 뒤 내려받아 주세요.");
  const document = copyEditorJson(state.undoableState);
  const parsed = parseNativeDocument(document);
  if (!parsed)
    throw new Error("편집 자료의 항목·원본·트랙 연결을 확인하지 못했습니다.");
  const missing = portableAssets(parsed);
  if (missing.length)
    throw new Error(
      `다시 열 수 있는 원본 주소가 없는 파일이 있습니다: ${missing.slice(0, 3).join(", ")}. 원본 연결을 완료한 뒤 편집 파일을 내려받아 주세요.`,
    );
  const p = options.previous;
  return createNativeEnvelope({
    extraEnvelope: p,
    savedAt: options.savedAt,
    document,
    scope: p?.scope ?? null,
    sourceSnapshotHash: p?.sourceSnapshotHash ?? null,
    sourceContext: p?.sourceContext ?? {},
    settings: {
      ...p?.settings,
      isLinkingEnabled: state.isLinkingEnabled,
      isSnappingEnabled: state.isSnappingEnabled,
      isSkimmingEnabled: state.isSkimmingEnabled,
      loop: state.loop,
      brandTemplateId: state.brandTemplateId,
      editorAppliedAccountBrandTemplateId:
        state.editorAppliedAccountBrandTemplateId,
      safeZoneGuide: {
        ...(record(p?.settings.safeZoneGuide) ? p.settings.safeZoneGuide : {}),
        ...state.safeZoneGuide,
      },
      projectType: options.projectType,
    },
  });
}

export type NativeFileRead =
  | { status: "not-native" }
  | { status: "error"; message: string }
  | { status: "ready"; value: NativeFileState };
export function readNativeDocumentFile(raw: unknown): NativeFileRead {
  try {
    let read = readNativeEnvelope(raw);
    if (
      read.kind === "other" &&
      record(raw) &&
      ["format", "engine", "schemaVersion"].some((key) =>
        Object.prototype.hasOwnProperty.call(raw, key),
      )
    )
      return {
        status: "error",
        message:
          "지원하지 않는 편집 파일 형식입니다. 이전 형식으로 바꿔 읽지 않았으며 원본 파일은 그대로입니다.",
      };
    if (read.kind === "other" && record(raw) && raw.auditSchema === 1) {
      // Older DEV audit files contain a native document but no editing preferences.
      const d = copyEditorJson(raw.document);
      read = {
        kind: "native",
        envelope: createNativeEnvelope({
          savedAt: "1970-01-01T00:00:00.000Z",
          document: d,
          settings: defaults(),
          sourceContext: {
            importedFrom: "audit-v1",
            auditFrame: finite(raw.frame) ? raw.frame : 0,
          },
        }),
      };
    }
    if (read.kind === "other") return { status: "not-native" };
    if (read.kind !== "native")
      return { status: "error", message: read.reason };
    if (read.envelope.engine.remotionVersion !== NATIVE_EDITOR_RUNTIME)
      return {
        status: "error",
        message:
          "이 편집 파일은 다른 버전의 정밀 편집 화면이 필요합니다. 원본 파일은 변경하지 않았습니다.",
      };
    const document = parseNativeDocument(read.envelope.document),
      settings = readSettings(read.envelope.settings);
    if (!document || !settings)
      return {
        status: "error",
        message: "편집 파일의 항목이나 설정이 올바르지 않습니다.",
      };
    const missing = portableAssets(document);
    if (missing.length)
      return {
        status: "error",
        message: `다시 연결해야 할 원본이 있습니다: ${missing.slice(0, 3).join(", ")}. 편집 파일은 변경하지 않았습니다.`,
      };
    return {
      status: "ready",
      value: { document, settings, envelope: read.envelope },
    };
  } catch (error) {
    return {
      status: "error",
      message:
        error instanceof Error ? error.message : "편집 파일을 읽지 못했습니다.",
    };
  }
}
